import { Button, message, Space } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import ReactSignatureCanvas from 'react-signature-canvas'

// react-signature-canvas uses CJS with __esModule flag, which can cause
// the default import to resolve to the module object instead of the component
const SignatureCanvas =
  'default' in ReactSignatureCanvas
    ? (
        ReactSignatureCanvas as unknown as {
          default: typeof ReactSignatureCanvas
        }
      ).default
    : ReactSignatureCanvas
import styled from 'styled-components'

const SignatureContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1rem;
  width: 25rem;
`

/**
 * The canvas takes its size from CSS and lets react-signature-canvas set the
 * bitmap to match (times devicePixelRatio). Giving it pixel width/height
 * attributes instead lets the global `canvas { max-width: 100% }` rule shrink
 * it on screen, and every stroke then lands offset from the cursor.
 */
const CanvasContainer = styled.div<{ $disabled?: boolean; $height: number }>`
  border: 1px solid #d9d9d9;
  border-radius: 0.125rem;
  margin-bottom: 0.5rem;
  background: #fff;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

  .signature-canvas {
    width: 100%;
    height: ${(props) => props.$height}px;
    touch-action: none;
  }
`

const Instructions = styled.div`
  margin-bottom: 0.5rem;
  color: #666;
  font-size: 0.75rem;
`

interface SignaturePadProps {
  /** Shows a Save Signature button that hands over the drawing on click. */
  onSign?: (signature: string) => void
  /** Reports the drawing after every stroke, and undefined when cleared. */
  onDrawingChange?: (signature: string | undefined) => void
  height?: number
  disabled?: boolean
  initialSignature?: string
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSign,
  onDrawingChange,
  height = 200,
  disabled = false,
  initialSignature,
}) => {
  const sigPadRef = useRef<ReactSignatureCanvas>(null)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    if (initialSignature && sigPadRef.current) {
      const img = new Image()
      img.onload = () => {
        const canvas = sigPadRef.current?.getCanvas()
        if (canvas) {
          const ctx = canvas.getContext('2d')
          ctx?.drawImage(img, 0, 0)
          setHasSignature(true)
        }
      }
      img.src = initialSignature
    }
  }, [initialSignature])

  const handleClear = () => {
    sigPadRef.current?.clear()
    setHasSignature(false)
    onDrawingChange?.(undefined)
  }

  const handleBegin = () => {
    setHasSignature(true)
  }

  const handleEnd = () => {
    onDrawingChange?.(sigPadRef.current?.toDataURL())
  }

  const handleSave = () => {
    if (!hasSignature || sigPadRef.current?.isEmpty()) {
      message.warning('Please provide a signature before saving')
      return
    }
    const signatureData = sigPadRef.current?.toDataURL()
    if (signatureData) {
      onSign?.(signatureData)
    } else {
      message.error('Failed to capture signature')
    }
  }

  return (
    <SignatureContainer>
      <Instructions>
        Use your mouse or touch screen to sign below. Click or tap and drag to
        create your signature.
      </Instructions>
      <CanvasContainer $disabled={disabled} $height={height}>
        <SignatureCanvas
          ref={sigPadRef}
          canvasProps={{ className: 'signature-canvas' }}
          onBegin={handleBegin}
          onEnd={handleEnd}
        />
      </CanvasContainer>
      <Space>
        <Button onClick={handleClear} disabled={!hasSignature || disabled}>
          Clear
        </Button>
        {onSign && (
          <Button onClick={handleSave} disabled={!hasSignature || disabled}>
            Save Signature
          </Button>
        )}
      </Space>
    </SignatureContainer>
  )
}
